export const DEFAULT_CHESS_MODEL_PARAMETERS = Object.freeze({
  baseDrawProbability: 0.09,
  referenceElo: 1800,
  eloStep: 200,
  eloRelativeEffect: 0.444,
  referenceMinutes: 5,
  timeRelativeEffect: 0.38,
  defaultPlayerDrawFactor: 1,
  whiteAdvantage: 0.52,
});

export const LEGACY_EXCEL_CHESS_MODEL_PARAMETERS = Object.freeze({
  whiteAdvantage: 0.52,
  eloScale: 400,
  drawEloInfluence: 0.0004,
  drawEloConstant: 0.1,
  drawTimeInfluence: 0.0067,
  drawTimeConstant: 0.1,
  drawDivisor: 3,
  defaultPlayerDrawCoefficient: 0.5,
});

const clamp = (value, minimum, maximum) => Math.min(maximum, Math.max(minimum, value));
const numberOr = (value, fallback) => {
  if (value === "" || value === null || value === undefined) return fallback;
  const number = Number(value);
  return Number.isFinite(number) ? number : fallback;
};
const positiveNumberOr = (value, fallback) => {
  const number = numberOr(value, fallback);
  return number > 0 ? number : fallback;
};
const eloExpectation = (elo1, elo2, scale) => 1 / (1 + 10 ** ((elo2 - elo1) / scale));
const applyWhiteAdvantage = (expectation, player1Color, whiteAdvantage) => {
  const adjustment = positiveNumberOr(whiteAdvantage, 0.5) / 0.5;
  if (player1Color === "white") return clamp(expectation * adjustment, 0, 1);
  if (player1Color === "black") return clamp(expectation / adjustment, 0, 1);
  return expectation;
};

// Active transitional strategy. Probabilistic betty strengths are not initialized here.
export const CHESS_MODEL_STRATEGY = "elo-original-normal";
export const SIGMA_DIFF = 200 * Math.SQRT2;

// Normal tail via the regularized incomplete gamma function (shape 1/2).
// Series near zero, continued fraction in the tail: no logistic approximation.
export function standardNormalCdf(z) {
  if (!Number.isFinite(z)) {
    if (z === Infinity) return 1 - Number.EPSILON / 2;
    if (z === -Infinity) return Number.MIN_VALUE;
    throw new Error("Normal CDF requires a numeric argument.");
  }
  if (z === 0) return 0.5;
  const x = (Math.abs(z) / Math.SQRT2) ** 2;
  let tail;
  if (x < 1.5) {
    let term = 2;
    let sum = term;
    for (let n = 1; n < 200; n += 1) {
      term *= x / (n + 0.5);
      sum += term;
      if (Math.abs(term) < Math.abs(sum) * Number.EPSILON) break;
    }
    tail = (1 - sum * Math.exp(-x + 0.5 * Math.log(x) - 0.5 * Math.log(Math.PI))) / 2;
  } else if (!Number.isFinite(x)) {
    tail = 0;
  } else {
    const tiny = 1e-300;
    let b = x + 0.5;
    let c = 1 / tiny;
    let d = 1 / b;
    let h = d;
    for (let n = 1; n < 200; n += 1) {
      const a = -n * (n - 0.5);
      b += 2;
      d = a * d + b;
      if (Math.abs(d) < tiny) d = tiny;
      c = b + a / c;
      if (Math.abs(c) < tiny) c = tiny;
      d = 1 / d;
      const delta = d * c;
      h *= delta;
      if (Math.abs(delta - 1) < 4 * Number.EPSILON) break;
    }
    tail = Math.exp(-x + 0.5 * Math.log(x) - 0.5 * Math.log(Math.PI)) * h / 2;
  }
  // IEEE-754 cannot represent arbitrarily small tails or 1 minus such tails.
  // Preserve open endpoints at the nearest representable interior number;
  // this is numerical representation, never a rating-difference cap.
  if (z < 0) return tail || Number.MIN_VALUE;
  const value = 1 - tail;
  return value === 1 ? 1 - Number.EPSILON / 2 : value;
}

export function inverseStandardNormalCdf(probability) {
  if (!(probability > 0 && probability < 1)) {
    throw new Error("White expected score must be strictly between zero and one.");
  }
  let lower = -40;
  let upper = 40;
  for (let iteration = 0; iteration < 100; iteration += 1) {
    const middle = (lower + upper) / 2;
    if (standardNormalCdf(middle) < probability) lower = middle;
    else upper = middle;
  }
  return (lower + upper) / 2;
}

export function calculateEqualStrengthDrawProbability({
  averageElo,
  effectiveMinutes,
  player1DrawFactor,
  player2DrawFactor,
  parameters = DEFAULT_CHESS_MODEL_PARAMETERS,
}) {
  const model = { ...DEFAULT_CHESS_MODEL_PARAMETERS, ...parameters };
  const referenceElo = numberOr(model.referenceElo, 1800);
  const eloStep = positiveNumberOr(model.eloStep, 200);
  const referenceMinutes = positiveNumberOr(model.referenceMinutes, 5);
  const minutes = positiveNumberOr(effectiveMinutes, referenceMinutes);
  const eloSteps = (numberOr(averageElo, referenceElo) - referenceElo) / eloStep;
  const eloMultiplier = Math.max(0, 1 + numberOr(model.eloRelativeEffect, 0) * eloSteps);
  const timeDoublings = Math.log2(minutes / referenceMinutes);
  const timeMultiplier = Math.max(0, 1 + numberOr(model.timeRelativeEffect, 0) * timeDoublings);
  const defaultFactor = numberOr(model.defaultPlayerDrawFactor, 1);
  const individualFactor = Math.max(0, (
    numberOr(player1DrawFactor, defaultFactor) + numberOr(player2DrawFactor, defaultFactor)
  ) / 2);

  return clamp(
    numberOr(model.baseDrawProbability, 0) * eloMultiplier * timeMultiplier * individualFactor,
    0,
    1,
  );
}

let cachedWhiteScore;
let cachedWhiteBonus;
const normalWhiteBonus = (score) => {
  if (score !== cachedWhiteScore) {
    cachedWhiteBonus = SIGMA_DIFF * inverseStandardNormalCdf(score);
    cachedWhiteScore = score;
  }
  return cachedWhiteBonus;
};

export function calculateChessProbabilities({
  player1Elo,
  player2Elo,
  player1Color = "white",
  effectiveMinutes,
  player1DrawFactor,
  player2DrawFactor,
  parameters = DEFAULT_CHESS_MODEL_PARAMETERS,
}) {
  const model = { ...DEFAULT_CHESS_MODEL_PARAMETERS, ...parameters };
  const elo1 = numberOr(player1Elo, model.referenceElo);
  const elo2 = numberOr(player2Elo, model.referenceElo);
  const effectiveEloDifference = elo1 - elo2;
  // whiteAdvantage is the persisted/UI name of whiteExpectedScore.
  const whiteExpectedScore = Number(parameters.whiteExpectedScore ?? model.whiteAdvantage);
  const whiteEloBonus = normalWhiteBonus(whiteExpectedScore);
  const colorAdjustment = player1Color === "white" ? whiteEloBonus
    : player1Color === "black" ? -whiteEloBonus : 0;
  const adjustedDifference = effectiveEloDifference + colorAdjustment;
  const expectation = standardNormalCdf(effectiveEloDifference / SIGMA_DIFF);
  const adjustedExpectation = standardNormalCdf(adjustedDifference / SIGMA_DIFF);
  const equalStrengthDrawProbability = calculateEqualStrengthDrawProbability({
    averageElo: (elo1 + elo2) / 2,
    effectiveMinutes,
    player1DrawFactor,
    player2DrawFactor,
    parameters: model,
  });
  const differenceFactor = 2 * Math.min(adjustedExpectation, 1 - adjustedExpectation);
  const draw = equalStrengthDrawProbability * differenceFactor;
  const player1Win = adjustedExpectation - draw / 2;
  const player2Win = 1 - adjustedExpectation - draw / 2;

  return {
    player1Win, draw, player2Win, expectation, adjustedExpectation,
    equalStrengthDrawProbability, differenceFactor, effectiveEloDifference,
    colorAdjustment, adjustedDifference, whiteEloBonus, modelStrategy: CHESS_MODEL_STRATEGY,
  };
}

export function calculateLegacyExcelChessProbabilities({
  player1Elo,
  player2Elo,
  player1Color = "white",
  effectiveMinutes,
  player1DrawCoefficient,
  player2DrawCoefficient,
  parameters = LEGACY_EXCEL_CHESS_MODEL_PARAMETERS,
}) {
  const model = { ...LEGACY_EXCEL_CHESS_MODEL_PARAMETERS, ...parameters };
  const elo1 = numberOr(player1Elo, 0);
  const elo2 = numberOr(player2Elo, 0);
  const expectation = eloExpectation(elo1, elo2, positiveNumberOr(model.eloScale, 400));
  const adjustedExpectation = applyWhiteAdvantage(expectation, player1Color, model.whiteAdvantage);
  const coefficient1 = numberOr(player1DrawCoefficient, model.defaultPlayerDrawCoefficient);
  const coefficient2 = numberOr(player2DrawCoefficient, model.defaultPlayerDrawCoefficient);
  const drawDistribution = (
    ((elo1 + elo2) / 2) * numberOr(model.drawEloInfluence, 0) +
    numberOr(model.drawEloConstant, 0) +
    numberOr(effectiveMinutes, 0) * numberOr(model.drawTimeInfluence, 0) +
    numberOr(model.drawTimeConstant, 0) +
    (coefficient1 + coefficient2) / 2
  ) / positiveNumberOr(model.drawDivisor, 1);
  const draw = 2 * drawDistribution * Math.min(1 - adjustedExpectation, adjustedExpectation);
  const player1Win = adjustedExpectation - draw / 2;
  return { player1Win, draw, player2Win: 1 - player1Win - draw, expectation, adjustedExpectation, drawDistribution };
}
